import {useState, type CSSProperties, type ReactNode} from 'react';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover';
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from '@/components/ui/command';
import {Button} from '@/components/app/button';
import {Check, ChevronsUpDown, X} from 'lucide-react';
import {cn} from '@/lib/utils';
import {readOptionLabel} from './inputs';

export interface ChoiceSelectProps<T = any, V = any> {
  options?: readonly T[];
  value?: V;
  onValueChange?: (value: V) => void;
  optionLabel?: string | ((option: T) => string);
  optionValue?: string;
  optionGroupLabel?: string;
  optionGroupChildren?: string;
  optionGroupTemplate?: (option: T) => ReactNode;
  itemTemplate?: (option: T) => ReactNode;
  valueTemplate?: (option: T, context: {placeholder: string}) => ReactNode;
  className?: string;
  panelClassName?: string;
  style?: CSSProperties;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  loading?: boolean;
  showClear?: boolean;
  filter?: boolean;
  name?: string;
  id?: string;
  inputId?: string;
  onBlur?: () => void;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}
export function ChoiceSelect<T, V>({
  options = [],
  value,
  onValueChange,
  optionLabel,
  optionValue,
  optionGroupChildren,
  optionGroupLabel,
  optionGroupTemplate,
  itemTemplate,
  valueTemplate,
  className,
  panelClassName,
  style,
  placeholder = 'Select',
  disabled,
  readOnly,
  loading,
  showClear,
  filter,
  name,
  id,
  inputId,
  onBlur,
  ...rest
}: ChoiceSelectProps<T, V>) {
  const [open, setOpen] = useState(false);
  // Forward only DOM-safe attributes (aria-*, data-*) to the trigger.
  const aria = Object.fromEntries(Object.entries(rest).filter(([key]) => /^(aria|data)-/.test(key)));
  // Options flattened in display order; a row's index is its key in both select variants.
  const groups = optionGroupChildren
    ? options.map((group: any) => ({group, items: (group[optionGroupChildren] ?? []) as T[]}))
    : [{group: undefined, items: [...options] as T[]}];
  const rows = groups.flatMap(({items}) => items);
  const groupHeading = (group: any) =>
    group === undefined
      ? undefined
      : (optionGroupTemplate?.(group) ?? (optionGroupLabel ? group[optionGroupLabel] : undefined));
  const getValue = (item: any): V => (optionValue ? item[optionValue] : item?.value !== undefined ? item.value : item);
  const getLabel = (item: any): string => readOptionLabel(item, optionLabel);
  const selected = rows.find((item) => Object.is(getValue(item), value));
  const render = (item: T) => itemTemplate?.(item) ?? getLabel(item);
  const choose = (key: string) => {
    const item = rows[Number(key)];
    if (item !== undefined) onValueChange?.(getValue(item));
    setOpen(false);
  };
  const searchable = filter || !!optionGroupChildren;
  const triggerClass = cn('w-full min-w-0', className);
  if (searchable)
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            id={inputId ?? id}
            name={name}
            disabled={disabled || readOnly || loading}
            className={cn(triggerClass, 'justify-between font-normal')}
            style={style}
            onBlur={onBlur}
            {...aria}
          >
            <span className="truncate">
              {selected !== undefined ? (valueTemplate?.(selected, {placeholder}) ?? getLabel(selected)) : placeholder}
            </span>
            <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className={cn('w-[var(--radix-popover-trigger-width)] p-0', panelClassName)}>
          <Command>
            <CommandInput placeholder="Search..." />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              {groups.map(({group, items}, groupIndex) => (
                <CommandGroup key={groupIndex} heading={groupHeading(group)}>
                  {items.map((item) => {
                    const index = rows.indexOf(item);
                    return (
                      <CommandItem
                        key={index}
                        value={`${index} ${getLabel(item)}`}
                        onSelect={() => choose(String(index))}
                      >
                        <Check className={cn('size-4', item !== selected && 'invisible')} />
                        {render(item)}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  return (
    <div className={cn(showClear && 'flex items-center gap-1', className)} style={style}>
      <Select
        value={selected === undefined ? '' : String(rows.indexOf(selected))}
        onValueChange={choose}
        name={name}
        disabled={disabled || readOnly || loading}
      >
        <SelectTrigger className={triggerClass} id={inputId ?? id} onBlur={onBlur} {...aria}>
          <SelectValue placeholder={placeholder}>
            {selected !== undefined && (valueTemplate?.(selected, {placeholder}) ?? getLabel(selected))}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className={panelClassName}>
          {rows.map((item, index) => (
            <SelectItem key={index} value={String(index)}>
              {render(item)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {showClear && selected !== undefined && (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Clear selection"
          disabled={disabled || readOnly}
          onClick={() => onValueChange?.(null as V)}
        >
          <X />
        </Button>
      )}
    </div>
  );
}
export function ChoiceMultiSelect<T, V>({
  value = [],
  onValueChange,
  options = [],
  optionLabel,
  optionValue,
  className,
  placeholder = 'Select',
  disabled,
  inputId,
  id,
  itemTemplate,
  selectedItemTemplate,
  ...props
}: ChoiceMultiSelectProps<T, V>) {
  const getValue = (item: any): V => (optionValue ? item[optionValue] : (item?.value ?? item));
  const getLabel = (item: any) => readOptionLabel(item, optionLabel);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          id={inputId ?? id}
          variant="outline"
          // Grows with its chips, so a large radius instead of a full pill keeps multi-line values readable.
          className={cn('h-auto min-h-9 w-full justify-between rounded-3xl px-4 font-normal', className)}
          disabled={disabled}
        >
          <span className="flex flex-wrap gap-1">
            {value.length
              ? options
                  .filter((item) => value.includes(getValue(item)))
                  .map((item, index) => (
                    <span className="rounded-full bg-muted px-2" key={index}>
                      {selectedItemTemplate?.(getValue(item)) ?? getLabel(item)}
                    </span>
                  ))
              : placeholder}
          </span>
          <ChevronsUpDown className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command>
          <CommandInput placeholder="Search..." />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((item, index) => {
                const key = getValue(item);
                const selected = value.includes(key);
                return (
                  <CommandItem
                    key={index}
                    value={`${index} ${getLabel(item)}`}
                    onSelect={() => onValueChange?.(selected ? value.filter((v) => v !== key) : [...value, key])}
                  >
                    <Check className={cn('size-4', !selected && 'invisible')} />
                    {itemTemplate?.(item) ?? getLabel(item)}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export type ChoiceMultiSelectProps<T = any, V = any> = Omit<ChoiceSelectProps<T, V[]>, 'valueTemplate'> & {
  selectedItemTemplate?: (value: V) => ReactNode;
  showSelectAll?: boolean;
};
